import React, { useState, useRef, useEffect } from "react";
import {
  Stack,
  TextField,
  useTheme,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { Link } from "react-router-dom";

import { GradientBorderBox } from "../../../../Components";
import { useGetProductsQuery } from "../../../../Store/Slices/rtk";

export default function SearchBox() {
  const theme = useTheme();

  const [openMobile, setOpenMobile] = useState(false);
  const [inp, setInp] = useState("");
  const searchContainerRef = useRef(null);

  const { data, isLoading } = useGetProductsQuery(
    `?page=1&single_data=false&no_of_docs_each_page=10&search=${inp}`,
    { skip: inp?.length <= 2 },
  );

  const handleInpChange = (e) => {
    setInp(e?.target?.value);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef?.current &&
        !searchContainerRef?.current?.contains(event?.target)
      ) {
        setInp("");
        setOpenMobile(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render search results (Max 7 items)
  const renderedSearchItems = data?.data?.slice(0, 7)?.map((product) => {
    const slug = product?.SEO?.slug
      ? `/${product?.SEO?.slug?.trim()?.replaceAll(" ", "-")}`
      : "";
    const targetUrl = `/product-details/${product?.code}/${product?.variant?.[0]?.code}${slug}`;

    return (
      <Link
        key={product?.code}
        to={targetUrl}
        onClick={() => {
          setInp("");
          setOpenMobile(false);
        }}
        style={{ textDecoration: "none", color: "inherit", display: "block" }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            px: "12px",
            py: "10px",
            borderRadius: "10px",
            transition: "all 0.2s ease-in-out",
            gap: "1rem",
            color: theme.palette.text.secondary,
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              transform: "translateX(-4px)",
            },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.85rem", xlg: "0.95rem" },
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {product?.name}
          </Typography>
          <KeyboardArrowLeftIcon
            sx={{
              fontSize: { xs: "18px", xlg: "22px" },
              transition: "transform 0.2s",
              ".MuiStack-root:hover &": {
                transform: "translateX(-4px)",
              },
            }}
          />
        </Stack>
      </Link>
    );
  });

  return (
    <Stack
      ref={searchContainerRef}
      direction="row"
      sx={{ mx: ".5rem", gap: "0.5rem", alignItems: "center" }}
    >
      <Stack
        sx={{
          position: { xs: "absolute", xlg: "static" },
          left: "50%",
          top: "calc(100% + 1.5rem)",
          transform: { xs: "translateX(-50%)", xlg: "translateX(0%)" },
          transition: "all 0.3s ease",
          opacity: { xs: openMobile ? 1 : 0, xlg: 1 },
          visibility: {
            xs: openMobile ? "visible" : "hidden",
            xlg: "visible",
          },
          zIndex: 10,
        }}
      >
        <Stack sx={{ position: "relative" }}>
          {/* Search Input */}
          <TextField
            size="small"
            placeholder="جستجوی محصولات..."
            autoComplete="off"
            onChange={handleInpChange}
            value={inp}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: alpha(theme.palette.primary.main, 0.7),
                      mr: "0.5rem",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "280px", xlg: "320px" },
              "& .MuiOutlinedInput-root": {
                // bgcolor: alpha(theme.palette.background.paper, 0.2),
                backdropFilter: "blur(4px)",
                borderRadius: "2rem",
                fontSize: "0.85rem",
                transition: "all 0.3s ease",
                "& fieldset": { border: "none" },
                "&:hover": {
                  bgcolor: theme.palette.background.paper,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
                },
                "&.Mui-focused": {
                  bgcolor: theme.palette.background.paper,
                  boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.15)}`,
                  "& fieldset": {
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                },
              },
            }}
          />

          {/* Search Results Dropdown */}
          <Stack
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              maxHeight: "350px",
              mt: "12px",
              borderRadius: "16px",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(20px)",
              boxShadow: `0 10px 40px ${alpha(theme.palette.common.black, 0.08)}`,
              padding: "1rem",
              display: inp?.length <= 0 ? "none" : "flex",
              overflowY: "auto",
              zIndex: 20,

              /* Custom Scrollbar */
              scrollbarWidth: "thin",
              scrollbarColor: `${alpha(theme.palette.primary.main, 0.5)} transparent`,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.5),
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <Stack sx={{ gap: "0.5rem" }}>
              {inp?.length <= 2 ? (
                <Typography
                  sx={{
                    textAlign: "center",
                    color: theme.palette.text.disabled,
                    py: 2,
                  }}
                >
                  حداقل سه کاراکتر وارد کنید...
                </Typography>
              ) : isLoading ? (
                <Stack alignItems="center" py={3}>
                  <CircularProgress
                    size="24px"
                    sx={{ color: theme.palette.primary.main }}
                  />
                </Stack>
              ) : renderedSearchItems?.length <= 0 ? (
                <Typography
                  sx={{
                    textAlign: "center",
                    color: theme.palette.text.disabled,
                    py: 2,
                  }}
                >
                  محصولی یافت نشد!
                </Typography>
              ) : (
                renderedSearchItems
              )}
            </Stack>
          </Stack>

          <GradientBorderBox
            bgGrad={`linear-gradient(90deg, ${theme.palette.background.default} -30%, ${theme.palette.background.paper} 40%, ${theme.palette.background.paper} 60%, ${theme.palette.primary.main} 130%)`}
          />
        </Stack>
      </Stack>

      {/* Mobile Search Toggle */}
      <IconButton
        onClick={() => setOpenMobile(!openMobile)}
        sx={{
          position: "relative",
          padding: "0.4rem",
          display: { xs: "flex", xlg: "none" },
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          transition: "all 0.3s",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          },
        }}
      >
        <SearchIcon sx={{ color: theme.palette.primary.main }} />
        <GradientBorderBox borderSize="0.15rem" />
      </IconButton>
    </Stack>
  );
}
